import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useState, useEffect, useContext } from "react";
import AuthContext from "../../../context/AuthContext";
import TerminalsFields from "../enrollment/TerminalsFields";
import { enrollmentTerminal } from "../../../services/terminal/enrollmentTerminalService";
import {
  ensureTerminalIds,
  generateTerminalId,
} from "../../../utils/terminalId";

export default function AddTerminalDialog({
  open,
  onClose,
  merchant,
  onCreated,
  setSnackOpen,
  setMessageOpen,
  setSeverity,
}) {
  const { acquirerData, token } = useContext(AuthContext);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !merchant) return;
    const clone = JSON.parse(JSON.stringify(merchant));

    if (!clone.fiat) clone.fiat = { currencyCode: "BRL" };
    if (!clone.acquirer) clone.acquirer = {};
    if (!clone.acquirer.terminal) clone.acquirer.terminal = {};
    clone.acquirer.terminal.terminals = [
      {
        terminalType: "",
        terminalID: generateTerminalId(),
        terminalSerial: "",
      },
    ];

    setFormData(clone);
  }, [open, merchant]);

  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

  const handleTerminalChange = (idx, field, value) =>
    setFormData((prev) => {
      const copy = deepClone(prev);
      copy.acquirer.terminal.terminals[idx][field] = value;

      if (
        field === "terminalType" &&
        value &&
        !copy.acquirer.terminal.terminals[idx].terminalID
      ) {
        copy.acquirer.terminal.terminals[idx].terminalID = generateTerminalId();
      }

      return copy;
    });

  const handleAddTerminal = () =>
    setFormData((prev) => {
      const copy = deepClone(prev);
      copy.acquirer.terminal.terminals.push({
        terminalType: "",
        terminalID: generateTerminalId(),
        terminalSerial: "",
      });
      return copy;
    });

  const handleDeleteTerminal = (idx) =>
    setFormData((prev) => {
      const copy = deepClone(prev);
      copy.acquirer.terminal.terminals.splice(idx, 1);
      return copy;
    });

  const handleSave = async () => {
    if (!formData) return;
    setSaving(true);

    const terminalsWithIds = ensureTerminalIds(
      formData.acquirer.terminal.terminals
    );

    if (
      terminalsWithIds.some(
        (terminal) =>
          !terminal.terminalID ||
          !terminal.terminalType ||
          !terminal.terminalSerial
      )
    ) {
      setSnackOpen(true);
      setMessageOpen("Please fill in all terminal information before saving!");
      setSeverity("error");
      setSaving(false);
      return;
    }

    const submissionData = {
      ...formData,
      acquirer: {
        ...formData.acquirer,
        terminal: {
          ...formData.acquirer.terminal,
          terminals: terminalsWithIds,
        },
      },
    };

    try {
      let resp = await enrollmentTerminal(
        "/edit",
        {
          acquirer: submissionData.acquirer,
          fiat: submissionData.fiat,
          reqhashMAC: submissionData.reqhashMAC,
          version: submissionData.version,
        },
        token,
        acquirerData
      );
      const updatedTerminals = resp.data?.acquirer?.terminal?.terminals ?? [];

      setSnackOpen(true);
      setMessageOpen("Terminal registered successfully!");
      setSeverity("success");

      onCreated(updatedTerminals);
      onClose();
    } catch {
      setSnackOpen(true);
      setMessageOpen("Error registering terminal");
      setSeverity("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New Terminal</DialogTitle>

      <DialogContent dividers>
        {!formData ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={3}>
            <TerminalsFields
              formData={formData}
              handleTerminalChange={handleTerminalChange}
              handleAddTerminal={handleAddTerminal}
              handleDeleteTerminal={handleDeleteTerminal}
              disabled={false}
              setSeverity={setSeverity}
              setMessageOpen={setMessageOpen}
              setSnackOpen={setSnackOpen}
            />
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving || !formData}
          variant="contained"
        >
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
